'use client';

import React, { useMemo } from 'react';
import { IconJusticeHammer, IconLike } from '@repo/svgs';
import { Divider } from '@/components/ui/Divider';
import { ListCell } from '@/components/ui/ListCell';
import { useBuilding } from '../../../context/BuildingProvider';
import { formatDateReadable } from '@repo/utils';

interface EvictionRecord {
  courtIndexNumber: string;
  executionDate: string;
}

interface GroupedEviction {
  year: string;
  records: EvictionRecord[];
}

const EvictionItem = ({ record, isLast }: { record: EvictionRecord; isLast?: boolean }) => (
  <>
    <ListCell
      leadingIcon={<IconJusticeHammer className="w-6 h-6 text-caution" />}
      title={record.courtIndexNumber}
      subtitle="Court index number"
      trailingTitle={record.executionDate}
      trailingSubtitle="Execution date"
      trailing="none"
    />
    {!isLast && (
      <div className="px-2">
        <Divider />
      </div>
    )}
  </>
);

export function EvictionsModal() {
  const { building } = useBuilding();

  const groupedEvictions = useMemo(() => {
    const evictionsData = building?.openData?.evictions?.data;
    if (!evictionsData || typeof evictionsData !== 'object') return [];

    return Object.entries(evictionsData)
      .map(([year, records]) => ({
        year,
        records: (Array.isArray(records) ? records : []).map((record: any) => ({
          courtIndexNumber: record.courtIndexNumber || '-',
          executionDate: record.executionDate ? formatDateReadable(record.executionDate) : '-',
        })),
      }))
      .sort((a, b) => Number(b.year) - Number(a.year));
  }, [building]);

  const hasEvictions = groupedEvictions.length > 0;
  const evictionsCount = building.openDataSummary?.numEvictions || 0;

  // Mock data for building vs NYC average (would come from API in real implementation)
  const buildingAverage = building?.openData?.evictions?.summary?.perUnitBuilding ?? 0.26;
  const nycAverage = building?.openData?.evictions?.summary?.perUnitCity ?? 0.81;
  const isBetterThanAverage = buildingAverage < nycAverage;

  if (!hasEvictions) {
    return (
      <div className="flex flex-col gap-4 px-2">
        <IconJusticeHammer className="w-8 h-8 text-secondary" />
        <p className="text-body-2 text-primary">
          There are no legally registered evictions associated with this building.
        </p>
        <div className="bg-secondary rounded-2xl p-4">
          <p className="text-body-2 text-secondary">
            Evictions are pulled from NYC housing court records.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Overview Section */}
      <div className="flex flex-col gap-4 px-2">
        {/* Success Badge */}
        {isBetterThanAverage && (
          <div className="bg-green-50 rounded-xl px-3 py-2 flex items-center gap-2">
            <IconLike className="w-4 h-4 text-success" />
            <p className="text-body-2 text-success flex-1">
              Average evictions per unit for this building is better than the city average.
            </p>
          </div>
        )}

        {/* Metrics Comparison */}
        <div className="flex items-center gap-4">
          <div className="flex-1 flex flex-col gap-2">
            <p className="text-caption text-secondary">Building average</p>
            <div className="flex flex-col">
              <p className="text-heading-6 text-primary">{buildingAverage}</p>
              <p className="text-body-2 text-primary">Evictions per unit</p>
            </div>
          </div>

          <div className="w-0 h-16 flex items-center justify-center">
            <div className="w-16 rotate-90">
              <Divider />
            </div>
          </div>

          <div className="flex-1 flex flex-col gap-2">
            <p className="text-caption text-secondary">NYC average</p>
            <div className="flex flex-col">
              <p className="text-heading-6 text-primary">{nycAverage}</p>
              <p className="text-body-2 text-primary">Evictions per unit</p>
            </div>
          </div>
        </div>
      </div>

      {/* Eviction Records List */}
      <div className="flex flex-col gap-1">
        {groupedEvictions.map(group => (
          <React.Fragment key={group.year}>
            <div className="bg-primary px-2 py-3 rounded-lg">
              <p className="text-body-2-high text-primary">{group.year}</p>
            </div>
            {group.records.map((record, index) => (
              <EvictionItem
                key={`${group.year}-${index}`}
                record={record}
                isLast={index === group.records.length - 1}
              />
            ))}
            {/* Add divider between years except for the last group */}
            {group !== groupedEvictions[groupedEvictions.length - 1] && (
              <div className="px-2 py-0.5">
                <Divider />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Info Container */}
      <div className="px-2">
        <div className="bg-secondary rounded-2xl p-4">
          <p className="text-body-2 text-secondary">
            Evictions are pulled from NYC housing court records.
          </p>
        </div>
      </div>
    </div>
  );
}
